import { useState } from "react";
import { Header } from "@/components/Header";
import { Footer } from "@/components/Footer";
import { SEO } from "@/components/SEO";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Users, Clock, Wifi, Infinity, ArrowRight, BookOpen, MapPin, Building2, Filter, Search } from "lucide-react";
import { Link } from "react-router-dom";
import { Input } from "@/components/ui/input";

const features = [
  {
    icon: Infinity,
    title: "Unlimited Capacity",
    description: "No maximum occupancy. Everyone gets in, even during peak hours",
  },
  {
    icon: Clock,
    title: "24/7 Access",
    description: "Study at any time, no closing hours or restricted access",
  },
  {
    icon: Users,
    title: "Live Presence",
    description: "See other students studying and feel the motivation of shared focus",
  },
  {
    icon: Wifi,
    title: "Instant Join",
    description: "No booking required. Click and enter immediately",
  },
];

const provinces = [
  { code: "ALL", name: "All Provinces" },
  { code: "BC", name: "British Columbia" },
  { code: "AB", name: "Alberta" },
  { code: "SK", name: "Saskatchewan" },
  { code: "MB", name: "Manitoba" },
  { code: "ON", name: "Ontario" },
  { code: "QC", name: "Quebec" },
  { code: "NB", name: "New Brunswick" },
  { code: "NS", name: "Nova Scotia" },
  { code: "NL", name: "Newfoundland" },
];

const usStates = [
  { code: "ALL", name: "All States" },
  { code: "AZ", name: "Arizona" },
  { code: "CA", name: "California" },
  { code: "FL", name: "Florida" },
  { code: "IL", name: "Illinois" },
  { code: "IN", name: "Indiana" },
  { code: "MI", name: "Michigan" },
  { code: "MN", name: "Minnesota" },
  { code: "NJ", name: "New Jersey" },
  { code: "NY", name: "New York" },
  { code: "OH", name: "Ohio" },
  { code: "PA", name: "Pennsylvania" },
  { code: "TX", name: "Texas" },
  { code: "WA", name: "Washington" },
];

const usUniversities = [
  {
    name: "University of Central Florida",
    shortName: "UCF",
    slug: "university-of-central-florida",
    description: "John C. Hitt Library group rooms, quiet upper floors",
    location: "Orlando, FL",
    state: "FL",
  },
  {
    name: "Texas A&M University",
    shortName: "Texas A&M",
    slug: "texas-am-university",
    description: "Evans Library, West Campus Library, individual quiet rooms",
    location: "College Station, TX",
    state: "TX",
  },
  {
    name: "Ohio State University",
    shortName: "OSU",
    slug: "ohio-state-university",
    description: "Thompson Library 11 floors, Research Commons, noise zones",
    location: "Columbus, OH",
    state: "OH",
  },
  {
    name: "Florida International University",
    shortName: "FIU",
    slug: "florida-international-university",
    description: "Law Library group rooms, Green Library quiet zones",
    location: "Miami, FL",
    state: "FL",
  },
  {
    name: "University of Florida",
    shortName: "UF",
    slug: "university-of-florida",
    description: "Library West 18 group rooms, grad-only 6th floor",
    location: "Gainesville, FL",
    state: "FL",
  },
  {
    name: "New York University",
    shortName: "NYU",
    slug: "new-york-university",
    description: "Bobst Library, Stern lounges, Kimmel Center",
    location: "New York, NY",
    state: "NY",
  },
  {
    name: "University of Texas at Austin",
    shortName: "UT Austin",
    slug: "university-of-texas-austin",
    description: "PCL group rooms, Scholars Lounge, quiet floors",
    location: "Austin, TX",
    state: "TX",
  },
  {
    name: "Arizona State University",
    shortName: "ASU",
    slug: "arizona-state-university",
    description: "Hayden Library 30+ rooms, reservable and drop-in",
    location: "Tempe, AZ",
    state: "AZ",
  },
  {
    name: "University of Minnesota",
    shortName: "UMN",
    slug: "university-of-minnesota",
    description: "Walter Library, Wilson Library, Law Library rooms",
    location: "Minneapolis, MN",
    state: "MN",
  },
  {
    name: "Michigan State University",
    shortName: "MSU",
    slug: "michigan-state-university",
    description: "Main Library, Gast Business Library, silent 4th floor",
    location: "East Lansing, MI",
    state: "MI",
  },
  {
    name: "Rutgers University",
    shortName: "Rutgers",
    slug: "rutgers-university",
    description: "Alexander Library, Art Library, multi-campus system",
    location: "New Brunswick, NJ",
    state: "NJ",
  },
  {
    name: "University of Illinois Urbana-Champaign",
    shortName: "UIUC",
    slug: "university-of-illinois",
    description: "Grainger Library, Main Library stacks, LibCal booking",
    location: "Urbana, IL",
    state: "IL",
  },
  {
    name: "University of Washington",
    shortName: "UW",
    slug: "university-of-washington",
    description: "Suzzallo Reading Room, Odegaard collaborative rooms",
    location: "Seattle, WA",
    state: "WA",
  },
  {
    name: "Pennsylvania State University",
    shortName: "Penn State",
    slug: "penn-state-university",
    description: "Pattee-Paterno Libraries, Knowledge Commons",
    location: "University Park, PA",
    state: "PA",
  },
  {
    name: "Purdue University",
    shortName: "Purdue",
    slug: "purdue-university",
    description: "WALC study rooms, Hicks Library, 9 hours/week limit",
    location: "West Lafayette, IN",
    state: "IN",
  },
  {
    name: "University of Southern California",
    shortName: "USC",
    slug: "university-of-southern-california",
    description: "Norris Medical Library, Mudd Hall Philosophy Library",
    location: "Los Angeles, CA",
    state: "CA",
  },
  {
    name: "Indiana University Bloomington",
    shortName: "IU",
    slug: "indiana-university",
    description: "Wells Library West Tower, on-demand kiosk booking",
    location: "Bloomington, IN",
    state: "IN",
  },
  {
    name: "University of California, Los Angeles",
    shortName: "UCLA",
    slug: "ucla",
    description: "Young Research Library, Powell Reading Room",
    location: "Los Angeles, CA",
    state: "CA",
  },
  {
    name: "University of Michigan",
    shortName: "UMich",
    slug: "university-of-michigan",
    description: "Shapiro Library floor zones, soundproof booths",
    location: "Ann Arbor, MI",
    state: "MI",
  },
  {
    name: "University of California, Berkeley",
    shortName: "UC Berkeley",
    slug: "uc-berkeley",
    description: "Gardner Stacks, Moffitt Library, Doe Reading Room",
    location: "Berkeley, CA",
    state: "CA",
  },
];

const canadianUniversities = [
  // Toronto Area
  {
    name: "University of Toronto – St. George",
    shortName: "U of T St. George",
    slug: "university-of-toronto-st-george",
    description: "Robarts Library, Gerstein, Hart House study spaces",
    location: "Toronto, ON",
    province: "ON",
  },
  {
    name: "University of Toronto – Mississauga",
    shortName: "UTM",
    slug: "university-of-toronto-mississauga",
    description: "Maanjiwe Nendamowinan, Deerfield Hall, UTM Library",
    location: "Mississauga, ON",
    province: "ON",
  },
  {
    name: "University of Toronto – Scarborough",
    shortName: "UTSC",
    slug: "university-of-toronto-scarborough",
    description: "UTSC Library, Highland Hall study spaces",
    location: "Scarborough, ON",
    province: "ON",
  },
  {
    name: "York University",
    shortName: "York",
    slug: "york-university",
    description: "Scott Library, Bronfman Business Library, Frost Library",
    location: "Toronto, ON",
    province: "ON",
  },
  {
    name: "Toronto Metropolitan University",
    shortName: "TMU",
    slug: "toronto-metropolitan-university",
    description: "TMU Library quiet floors and group study rooms",
    location: "Toronto, ON",
    province: "ON",
  },
  {
    name: "OCAD University",
    shortName: "OCAD",
    slug: "ocad-university",
    description: "Library Learning Zone, creative work spaces",
    location: "Toronto, ON",
    province: "ON",
  },
  {
    name: "University of Guelph-Humber",
    shortName: "Guelph-Humber",
    slug: "university-of-guelph-humber",
    description: "North Library, Learning Commons, 24/7 spaces",
    location: "Toronto, ON",
    province: "ON",
  },
  {
    name: "Seneca College",
    shortName: "Seneca",
    slug: "seneca-college",
    description: "Newnham, King & Seneca@York campus libraries",
    location: "Toronto, ON",
    province: "ON",
  },
  {
    name: "Humber College",
    shortName: "Humber",
    slug: "humber-college",
    description: "North Library, Learning Resource Commons, Idea Lab",
    location: "Toronto, ON",
    province: "ON",
  },
  // Rest of Canada
  {
    name: "McGill University",
    shortName: "McGill",
    slug: "mcgill-university",
    description: "Redpath Library, study pods, Osler heritage reading room",
    location: "Montreal, QC",
    province: "QC",
  },
  {
    name: "University of British Columbia",
    shortName: "UBC",
    slug: "ubc-vancouver",
    description: "Irving K. Barber Centre, Ridington Room, Music Library",
    location: "Vancouver, BC",
    province: "BC",
  },
  {
    name: "University of Alberta",
    shortName: "U of A",
    slug: "university-of-alberta",
    description: "Cameron Library 24/7, Rutherford, noise-zoned spaces",
    location: "Edmonton, AB",
    province: "AB",
  },
  {
    name: "University of Calgary",
    shortName: "U of C",
    slug: "university-of-calgary",
    description: "TFDL workrooms, VR rooms, treadmill desks",
    location: "Calgary, AB",
    province: "AB",
  },
  {
    name: "University of Ottawa",
    shortName: "uOttawa",
    slug: "university-of-ottawa",
    description: "Morisset Library, CRX, FSS building study spaces",
    location: "Ottawa, ON",
    province: "ON",
  },
  {
    name: "McMaster University",
    shortName: "McMaster",
    slug: "mcmaster-university",
    description: "Mills Library, Thode Library, noise-zoned floors",
    location: "Hamilton, ON",
    province: "ON",
  },
  {
    name: "University of Waterloo",
    shortName: "Waterloo",
    slug: "university-of-waterloo",
    description: "Dana Porter, Davis Centre, SLC private rooms",
    location: "Waterloo, ON",
    province: "ON",
  },
  {
    name: "Western University",
    shortName: "Western",
    slug: "western-university",
    description: "Weldon Library, silent 4th floor, family room",
    location: "London, ON",
    province: "ON",
  },
  {
    name: "Queen's University",
    shortName: "Queen's",
    slug: "queens-university",
    description: "Stauffer Library, Bracken Health Sciences Library",
    location: "Kingston, ON",
    province: "ON",
  },
  {
    name: "University of Manitoba",
    shortName: "U of M",
    slug: "university-of-manitoba",
    description: "Elizabeth Dafoe Library, Quiet Pods, group rooms",
    location: "Winnipeg, MB",
    province: "MB",
  },
  {
    name: "University of Saskatchewan",
    shortName: "USask",
    slug: "university-of-saskatchewan",
    description: "Murray Library, Low-Sensory Room, noise zones",
    location: "Saskatoon, SK",
    province: "SK",
  },
  {
    name: "Université Laval",
    shortName: "Laval",
    slug: "universite-laval",
    description: "Bonenfant Library, online-course rooms, silent study",
    location: "Quebec City, QC",
    province: "QC",
  },
  {
    name: "Université de Montréal",
    shortName: "UdeM",
    slug: "universite-de-montreal",
    description: "Sound-insulated cabins, innovation spaces, silent rooms",
    location: "Montreal, QC",
    province: "QC",
  },
  {
    name: "Dalhousie University",
    shortName: "Dalhousie",
    slug: "dalhousie-university",
    description: "Killam Library, Kellogg Library, Law Library",
    location: "Halifax, NS",
    province: "NS",
  },
  {
    name: "Simon Fraser University",
    shortName: "SFU",
    slug: "simon-fraser-university",
    description: "Bennett Library, floor-based noise zones, multi-campus",
    location: "Burnaby, BC",
    province: "BC",
  },
  {
    name: "Concordia University",
    shortName: "Concordia",
    slug: "concordia-university",
    description: "Webster Library, zero-noise room, Grey Nuns building",
    location: "Montreal, QC",
    province: "QC",
  },
  {
    name: "University of Victoria",
    shortName: "UVic",
    slug: "university-of-victoria",
    description: "McPherson Library, Tek Booths, walk-stations",
    location: "Victoria, BC",
    province: "BC",
  },
  {
    name: "Memorial University",
    shortName: "Memorial",
    slug: "memorial-university",
    description: "QEII Library, dissertation rooms, CNS reading room",
    location: "St. John's, NL",
    province: "NL",
  },
  {
    name: "Carleton University",
    shortName: "Carleton",
    slug: "carleton-university",
    description: "MacOdrum Library, silent/conversational floors, VR rooms",
    location: "Ottawa, ON",
    province: "ON",
  },
  {
    name: "University of New Brunswick",
    shortName: "UNB",
    slug: "university-of-new-brunswick",
    description: "Harriet Irving Library, LCD-equipped group rooms",
    location: "Fredericton, NB",
    province: "NB",
  },
];

const StudyRoom = () => {
  const [selectedCountry, setSelectedCountry] = useState<"CA" | "US">("CA");
  const [selectedProvince, setSelectedProvince] = useState("ALL");
  const [selectedState, setSelectedState] = useState("ALL");
  const [searchQuery, setSearchQuery] = useState("");

  const filteredUniversities = canadianUniversities.filter(uni => {
    const matchesProvince = selectedProvince === "ALL" || uni.province === selectedProvince;
    const matchesSearch = searchQuery === "" || 
      uni.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
      uni.shortName.toLowerCase().includes(searchQuery.toLowerCase()) ||
      uni.location.toLowerCase().includes(searchQuery.toLowerCase());
    return matchesProvince && matchesSearch;
  });

  const filteredUsUniversities = usUniversities.filter(uni => {
    const matchesState = selectedState === "ALL" || uni.state === selectedState;
    const matchesSearch = searchQuery === "" || 
      uni.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
      uni.shortName.toLowerCase().includes(searchQuery.toLowerCase()) ||
      uni.location.toLowerCase().includes(searchQuery.toLowerCase());
    return matchesState && matchesSearch;
  });

  const handleCountryChange = (country: "CA" | "US") => {
    setSelectedCountry(country);
    setSearchQuery("");
    setSelectedProvince("ALL");
    setSelectedState("ALL");
  };

  return (
    <div className="min-h-screen bg-background">
      <SEO 
        title="Study Room - 24/7 Virtual Study Spaces"
        description="Join free virtual study rooms with 24/7 access. Study with others online, unlimited capacity, no booking required. Stay focused and motivated."
        keywords="study room, virtual study room, online study room, study room online, free study room, study together"
        canonicalUrl="/study-room/"
      />
      <Header />
      
      <main>
        {/* Hero Section */}
        <section className="relative min-h-[90vh] flex items-center justify-center overflow-hidden">
          {/* Gradient Background */}
          <div className="absolute inset-0 bg-gradient-to-b from-background via-background to-muted" />
          
          {/* Animated Glow Effects */}
          <div className="absolute top-20 left-20 w-96 h-96 bg-primary/20 rounded-full blur-[120px] animate-glow-pulse" />
          <div className="absolute bottom-20 right-20 w-96 h-96 bg-secondary/20 rounded-full blur-[120px] animate-glow-pulse" style={{ animationDelay: '1s' }} />
          
          <div className="container relative z-10 mx-auto px-4 sm:px-6 lg:px-8">
            <div className="grid lg:grid-cols-2 gap-12 items-center">
              {/* Left Content */}
              <div className="text-left space-y-8 animate-fade-in">
                <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-card/50 border border-border backdrop-blur-sm">
                  <div className="w-2 h-2 bg-primary rounded-full animate-glow-pulse" />
                  <span className="text-sm font-medium text-muted-foreground">Always Open • Never Full</span>
                </div>
                
                <h1 className="font-display text-5xl sm:text-6xl lg:text-7xl font-bold leading-tight">
                  <span className="text-gradient">Study Room</span>
                </h1>
                
                <p className="text-xl sm:text-2xl text-muted-foreground font-light max-w-xl">
                  Join online study rooms 24/7 without waiting or booking. Study alongside others anytime, anywhere.
                </p>
                
                <div className="flex flex-col sm:flex-row gap-4 pt-4">
                  <Button 
                    size="lg" 
                    className="group bg-primary hover:bg-primary/90 text-primary-foreground font-semibold px-8 py-6 text-lg glow-primary transition-all hover:scale-105"
                    asChild
                  >
                    <a href="https://app.csw.live" target="_blank" rel="noopener noreferrer">
                      Enter Study Room
                      <ArrowRight className="ml-2 h-5 w-5 group-hover:translate-x-1 transition-transform" />
                    </a>
                  </Button>
                  <Button 
                    size="lg" 
                    variant="outline"
                    className="border-2 border-border hover:bg-card font-semibold px-8 py-6 text-lg transition-all hover:scale-105"
                    asChild
                  >
                    <a href="https://app.csw.live" target="_blank" rel="noopener noreferrer">
                      Browse Rooms
                    </a>
                  </Button>
                </div>
                
                <div className="flex items-center gap-8 pt-8">
                  <div>
                    <p className="text-3xl font-bold text-gradient">24/7</p>
                    <p className="text-sm text-muted-foreground">Always Open</p>
                  </div>
                  <div className="w-px h-12 bg-border" />
                  <div>
                    <p className="text-3xl font-bold text-gradient">∞</p>
                    <p className="text-sm text-muted-foreground">Unlimited Seats</p>
                  </div>
                  <div className="w-px h-12 bg-border" />
                  <div>
                    <p className="text-3xl font-bold text-gradient">Free</p>
                    <p className="text-sm text-muted-foreground">Forever</p>
                  </div>
                </div>
              </div>
              
              {/* Right Video */}
              <div className="relative animate-fade-in" style={{ animationDelay: '0.2s' }}>
                <div className="relative rounded-3xl overflow-hidden border border-border/50 shadow-2xl">
                  <video 
                    src="/videos/productivity-demo.mp4" 
                    autoPlay
                    loop
                    muted
                    playsInline
                    className="w-full h-auto"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-background/80 via-transparent to-transparent" />
                </div>
                
              </div>
            </div>
          </div>
        </section>
        {/* Universities Section */}
        <section className="py-24 px-4 relative overflow-hidden">
          <div className="absolute inset-0 bg-gradient-to-b from-muted/50 to-background" />
          <div className="absolute top-1/3 left-0 w-96 h-96 bg-primary/10 rounded-full blur-[120px]" />
          
          <div className="container mx-auto max-w-6xl relative z-10">
            <div className="text-center mb-12 animate-fade-in">
              <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-card/50 border border-border backdrop-blur-sm mb-6">
                <Building2 className="w-4 h-4 text-primary" />
                <span className="text-sm font-medium text-muted-foreground">
                  {selectedCountry === "CA" ? "29 Universities" : "Top 20 by Enrollment"}
                </span>
              </div>
              <h2 className="text-4xl md:text-5xl font-bold font-display mb-6">
                {selectedCountry === "CA" ? "Canadian" : "U.S."}{" "}
                <span className="text-gradient">University Study Rooms</span>
              </h2>
              <p className="text-xl text-muted-foreground max-w-2xl mx-auto">
                {selectedCountry === "CA" 
                  ? "Study room guides for 29 universities across Canada. Filter by province or search by name."
                  : "Study room guides for 20 major universities across the United States. Filter by state or search by name."
                }
              </p>
            </div>

            {/* Country Toggle */}
            <div className="flex justify-center mb-10">
              <div className="inline-flex rounded-xl bg-card/80 backdrop-blur-sm border border-border p-1.5">
                <button
                  onClick={() => handleCountryChange("CA")}
                  className={`px-6 py-3 rounded-lg font-semibold text-sm transition-all flex items-center gap-2 ${
                    selectedCountry === "CA"
                      ? "bg-primary text-primary-foreground shadow-lg"
                      : "text-muted-foreground hover:text-foreground hover:bg-muted/50"
                  }`}
                >
                  <span className="text-lg">🇨🇦</span>
                  Canada
                </button>
                <button
                  onClick={() => handleCountryChange("US")}
                  className={`px-6 py-3 rounded-lg font-semibold text-sm transition-all flex items-center gap-2 ${
                    selectedCountry === "US"
                      ? "bg-primary text-primary-foreground shadow-lg"
                      : "text-muted-foreground hover:text-foreground hover:bg-muted/50"
                  }`}
                >
                  <span className="text-lg">🇺🇸</span>
                  United States
                </button>
              </div>
            </div>

            {/* Region Filter */}
            <div className="flex flex-wrap justify-center gap-2 mb-10">
              {selectedCountry === "CA" ? (
                provinces.map((province) => (
                  <Button
                    key={province.code}
                    variant={selectedProvince === province.code ? "default" : "outline"}
                    size="sm"
                    onClick={() => setSelectedProvince(province.code)}
                    className={`transition-all ${
                      selectedProvince === province.code 
                        ? "bg-primary text-primary-foreground" 
                        : "bg-card/50 border-border/50 hover:border-primary/50 hover:bg-card"
                    }`}
                  >
                    {province.code === "ALL" && <Filter className="w-3 h-3 mr-1" />}
                    {province.name}
                  </Button>
                ))
              ) : (
                usStates.map((state) => (
                  <Button
                    key={state.code}
                    variant={selectedState === state.code ? "default" : "outline"}
                    size="sm"
                    onClick={() => setSelectedState(state.code)}
                    className={`transition-all ${
                      selectedState === state.code 
                        ? "bg-primary text-primary-foreground" 
                        : "bg-card/50 border-border/50 hover:border-primary/50 hover:bg-card"
                    }`}
                  >
                    {state.code === "ALL" && <Filter className="w-3 h-3 mr-1" />}
                    {state.name}
                  </Button>
                ))
              )}
            </div>

            {/* Search Bar */}
            <div className="max-w-md mx-auto mb-10">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                <Input
                  type="text"
                  placeholder={`Search ${selectedCountry === "CA" ? "Canadian" : "US"} universities by name or city...`}
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="pl-10 bg-card/50 border-border/50 focus:border-primary/50"
                />
              </div>
            </div>

            {/* Universities Grid */}
            {selectedCountry === "CA" ? (
              filteredUniversities.length === 0 ? (
                <div className="text-center py-12">
                  <p className="text-muted-foreground">No universities found for this province.</p>
                </div>
              ) : (
                <div className="grid md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
                  {filteredUniversities.map((uni) => (
                    <Link
                      key={uni.slug}
                      to={`/study-room/${uni.slug}/`}
                      className="group"
                    >
                      <Card className="h-full bg-card/50 backdrop-blur-sm border-border/50 hover:border-primary/50 transition-all hover:scale-105 hover:shadow-xl">
                        <CardContent className="p-5 space-y-3">
                          <div className="flex items-start gap-3">
                            <div className="w-9 h-9 rounded-lg bg-gradient-to-br from-primary/20 to-secondary/20 flex items-center justify-center group-hover:scale-110 transition-transform flex-shrink-0">
                              <MapPin className="w-4 h-4 text-primary" />
                            </div>
                            <div>
                              <h3 className="text-base font-semibold text-foreground font-display group-hover:text-primary transition-colors">
                                {uni.shortName}
                              </h3>
                              <p className="text-xs text-muted-foreground">
                                {uni.location}
                              </p>
                            </div>
                          </div>
                          <p className="text-muted-foreground text-xs leading-relaxed line-clamp-2">
                            {uni.description}
                          </p>
                          <div className="flex items-center text-primary text-xs font-medium">
                            View guide <ArrowRight className="ml-1 w-3 h-3 group-hover:translate-x-1 transition-transform" />
                          </div>
                        </CardContent>
                      </Card>
                    </Link>
                  ))}
                </div>
              )
            ) : (
              filteredUsUniversities.length === 0 ? (
                <div className="text-center py-12">
                  <p className="text-muted-foreground">No universities found for this state.</p>
                </div>
              ) : (
                <div className="grid md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
                  {filteredUsUniversities.map((uni) => (
                    <Link
                      key={uni.slug}
                      to={`/study-room/${uni.slug}/`}
                      className="group"
                    >
                      <Card className="h-full bg-card/50 backdrop-blur-sm border-border/50 hover:border-primary/50 transition-all hover:scale-105 hover:shadow-xl">
                        <CardContent className="p-5 space-y-3">
                          <div className="flex items-start gap-3">
                            <div className="w-9 h-9 rounded-lg bg-gradient-to-br from-secondary/20 to-primary/20 flex items-center justify-center group-hover:scale-110 transition-transform flex-shrink-0">
                              <MapPin className="w-4 h-4 text-primary" />
                            </div>
                            <div>
                              <h3 className="text-base font-semibold text-foreground font-display group-hover:text-primary transition-colors">
                                {uni.shortName}
                              </h3>
                              <p className="text-xs text-muted-foreground">
                                {uni.location}
                              </p>
                            </div>
                          </div>
                          <p className="text-muted-foreground text-xs leading-relaxed line-clamp-2">
                            {uni.description}
                          </p>
                          <div className="flex items-center text-primary text-xs font-medium">
                            View guide <ArrowRight className="ml-1 w-3 h-3 group-hover:translate-x-1 transition-transform" />
                          </div>
                        </CardContent>
                      </Card>
                    </Link>
                  ))}
                </div>
              )
            )}
          </div>
        </section>

        {/* Features Grid */}
        <section className="py-24 px-4 relative overflow-hidden">
          <div className="absolute inset-0 bg-gradient-to-b from-muted to-background" />
          <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[600px] h-[600px] bg-primary/10 rounded-full blur-[150px]" />
          
          <div className="container mx-auto max-w-6xl relative z-10">
            <div className="text-center mb-16 animate-fade-in">
              <h2 className="text-4xl md:text-5xl font-bold font-display mb-6">
                Always{" "}
                <span className="text-gradient">Accessible</span>
              </h2>
              <p className="text-xl text-muted-foreground max-w-2xl mx-auto">
                No more crowded study spaces or limited hours
              </p>
            </div>

            <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
              {features.map((feature) => {
                const Icon = feature.icon;
                return (
                  <Card
                    key={feature.title}
                    className="group bg-card/50 backdrop-blur-sm border-border/50 hover:border-primary/50 transition-all hover:scale-105 hover:shadow-xl text-center"
                  >
                    <CardContent className="p-8 space-y-4">
                      <div className="w-12 h-12 mx-auto rounded-xl bg-gradient-to-br from-primary/20 to-secondary/20 flex items-center justify-center group-hover:scale-110 transition-transform">
                        <Icon className="w-6 h-6 text-primary" />
                      </div>
                      
                      <h3 className="text-xl font-semibold text-foreground font-display">
                        {feature.title}
                      </h3>
                      
                      <p className="text-muted-foreground leading-relaxed">
                        {feature.description}
                      </p>
                    </CardContent>
                  </Card>
                );
              })}
            </div>
          </div>
        </section>

        {/* Problem Solution */}
        <section className="py-24 px-4 relative">
          <div className="absolute top-0 right-0 w-96 h-96 bg-secondary/10 rounded-full blur-[120px]" />
          
          <div className="max-w-4xl mx-auto relative z-10">
            <div className="text-center mb-12">
              <h2 className="text-4xl md:text-5xl font-bold font-display mb-6">
                No More{" "}
                <span className="text-gradient">Crowded Spaces</span>
              </h2>
            </div>
            
            <div className="p-8 rounded-2xl bg-card/50 backdrop-blur-sm border border-border/50">
              <p className="text-lg text-muted-foreground mb-6 leading-relaxed">
                Physical study rooms fill up fast during finals week and busy exam periods. Students waste time searching for available spaces, only to find everything booked or overcrowded.
              </p>
              <p className="text-xl font-semibold text-foreground mb-6">
                CSW solves this problem completely.
              </p>
              <p className="text-lg text-muted-foreground leading-relaxed">
                Our online study rooms are always available, never full, and accessible 24/7 from anywhere. No booking systems. No capacity limits. No closed doors.
              </p>
            </div>
          </div>
        </section>

        {/* How It Works */}
        <section className="py-24 px-4 relative overflow-hidden">
          <div className="absolute inset-0 bg-gradient-to-b from-muted/50 to-background" />
          
          <div className="max-w-4xl mx-auto relative z-10">
            <div className="text-center mb-16">
              <h2 className="text-4xl md:text-5xl font-bold font-display mb-6">
                Simple to{" "}
                <span className="text-gradient">Join</span>
              </h2>
            </div>
            
            <div className="space-y-8">
              {[
                { step: 1, title: "Pick Your Room", desc: "Browse available study rooms by mode (Chill, Study, Work) or vibe" },
                { step: 2, title: "Enter Instantly", desc: "Join with one click—no waiting, no approval, no capacity check" },
                { step: 3, title: "Study Together", desc: "See others working, stay motivated, and build productive habits" },
              ].map((item, index) => (
                <div 
                  key={index}
                  className="flex gap-6 items-start p-6 rounded-2xl bg-card/50 backdrop-blur-sm border border-border/50 hover:border-primary/50 transition-all hover:shadow-lg group"
                >
                  <div className="flex-shrink-0 w-14 h-14 rounded-2xl bg-gradient-to-br from-primary to-secondary text-primary-foreground flex items-center justify-center font-bold text-xl font-display group-hover:scale-110 transition-transform">
                    {item.step}
                  </div>
                  <div>
                    <h3 className="text-xl font-semibold mb-2 font-display">{item.title}</h3>
                    <p className="text-muted-foreground">{item.desc}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* CTA Section */}
        <section className="py-24 px-4 relative overflow-hidden">
          <div className="absolute inset-0 bg-gradient-to-b from-background via-primary/5 to-background" />
          <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[800px] h-[400px] bg-primary/20 rounded-full blur-[150px]" />
          
          <div className="max-w-4xl mx-auto text-center relative z-10">
            <h2 className="text-4xl md:text-5xl font-bold mb-6 font-display">
              Your Study Room is{" "}
              <span className="text-gradient">Ready</span>
            </h2>
            <p className="text-xl text-muted-foreground mb-10 max-w-2xl mx-auto">
              Join thousands of students studying together online right now.
            </p>
            <Button 
              size="lg" 
              className="group bg-primary hover:bg-primary/90 text-primary-foreground font-semibold px-10 py-7 text-lg glow-primary transition-all hover:scale-105"
              asChild
            >
              <a href="https://app.csw.live" target="_blank" rel="noopener noreferrer">
                Enter Now
                <ArrowRight className="ml-2 h-5 w-5 group-hover:translate-x-1 transition-transform" />
              </a>
            </Button>
          </div>
        </section>
      </main>

      <Footer />
    </div>
  );
};

export default StudyRoom;